import { useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { navigate, useLocation } from '@reach/router';
import { createStyles, makeStyles } from '@material-ui/core';
import ROUTES from '../../../routes';
import {
  AccountDetail,
  Channel,
  SaType,
  Tpa_AccountDetail,
  Tpa_MonthsMatrix,
  Mutation,
} from '../../../__generated__/pge-types';
import { ProgressTrackerStep } from '../../ProgressTracker';
import { useTranslation } from '../../../hooks/useTranslation';
import useAuthQuery from '../../../hooks/useAuthQuery';
import useSelectedAccountParams from '../../../hooks/useSelectedAccountParams';
import { getAccountDetailsQuery, enrollTPAMutation } from '../query';
import useUtil from '../useUtil';
import {
  OneTimePayConfirmationResponse,
  OneTimePayConfigData,
} from '../../payment-form/oneTimePayment.types';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import {
  calculateTpaAmounts,
  setFirstPaymentToCompleted,
} from './tpaAmountCalculationUtil';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import useGTMUtilNmttp from '../gtm/useGTMUtilNmttp';
import moment from 'moment';

export const DEFAULT_MONTHS = 12;
export const MAX_NUM_MONTHS_FOR_SHORT_TERM = 12;

export type PaymentObject = {
  oneTimePayConfirmationData: OneTimePayConfirmationResponse;
  oneTimePayConfig?: OneTimePayConfigData | null;
  paymentMethodCapLimit: number;
};

export type TpaAccountDetails = {
  isEligible: boolean;
  isEnrolled: boolean;
  accountBalance?: number;
  recommendedPaymentMonths: number;
  enrolledInstallmentDetails?: EnrolledInstallmentDetails;
};

type EnrolledInstallmentDetails = {
  remainingBalance: number;
  totalMonths: number;
  currentTpaMonthBalance?: number;
  monthsMatrix: TpaMonthsMatrix[];
};

export type TpaMonthsMatrix = {
  monthNumber: number;
  isPaymentCompleted: boolean;
  paymentDate: Date;
  monthlyAmount: number;
  doesBillExist?: Boolean;
};

interface Props {
  isTPAFlagOverwrite?: boolean | null;
  landingURL?: string;
  shouldRedirect?: boolean | null;
}

export default (props?: Props) => {
  //TODO: Once this is live, remove the feature flag and associated code incl. unit  tests.
  const shouldRedirect =
    props?.shouldRedirect !== (null || undefined)
      ? props?.shouldRedirect
      : true;
  const { isTPAEnabled } = useContext(featureFlagsContext);
  const isTPAFeatureEnabled =
    props?.isTPAFlagOverwrite !== (null || undefined)
      ? props?.isTPAFlagOverwrite
      : isTPAEnabled;

  const {
    gtm_TPAReviewPlan_HandleOnConfirmPlanClick,
    gtm_TPAReviewPlan_HandleOnConfirmPlanClickError,
    gtm_TPAMakePayment_HandleOnPaymentConfirmation,
    gtm_TPACreatePlan_HandleOnReviewPlanClick,
    gtm_TPACreatePlan_HandleCreatePlanOnBackClick,
  } = useGTMUtilNmttp();
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [installmentAmount, setInstallmentAmount] = useState(0);
  const [numberOfMonths, setNumberOfMonths] = useState<number>(DEFAULT_MONTHS);
  const [tpaConfirmationNumber, setTPAConfirmationNumber] = useState<number>(0);
  const [isTPAPlanSelected, setIsTPAPlanSelected] = useState<boolean>(false);
  const [tpaInstallmentPlan, setTPAInstallmentPlan] = useState<
    TpaMonthsMatrix[]
  >([]);
  const [
    oneTimePayConfirmationDataDetails,
    setOneTimePayConfirmationData,
  ] = useState<OneTimePayConfirmationResponse>();
  const [oneTimePayConfigDataDetails, setOneTimePayConfig] = useState<
    OneTimePayConfigData
  >();
  const [paymentMethodCapLimitData, setPaymentMethodCapLimit] = useState<
    number
  >(2);
  const [isShowTPAStepper, setIsShowTPAStepper] = useState(false);
  const [tpaDetails, setTpaDetails] = useState<TpaAccountDetails | undefined>(
    undefined,
  );
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [currentChargesDue, setCurrentChargesDue] = useState<
    number | undefined
  >();
  const [tpaDownPaymentAmount, setTPADownPaymentAmount] = useState<number>(0);

  const location = useLocation();
  const { t } = useTranslation();

  const { setErrorNotification } = useUtil();

  const useStyles = makeStyles(() =>
    createStyles({
      completed: {
        color: '#43936F !important',
      },
    }),
  );
  const classes = useStyles();

  const progressTrackerSteps: ProgressTrackerStep[] = [
    {
      isActive: location.pathname === ROUTES.TPA_CREATE_PLAN,
      isComplete: currentStep ? currentStep > 1 : false,
      label: t('TPA_STEPPER_LABEL_1'),
      stepIconClasses: {
        completed: classes.completed,
      },
    },
    {
      isActive: location.pathname === ROUTES.TPA_REVIEW_PLAN,
      isComplete: currentStep ? currentStep > 2 : false,
      label: t('TPA_STEPPER_LABEL_2'),
      stepIconClasses: {
        completed: classes.completed,
      },
    },
    {
      isActive: location.pathname === ROUTES.TPA_CONFIRM_PLAN,
      isComplete: currentStep ? currentStep > 3 : false,
      label: t('TPA_STEPPER_LABEL_3'),
      stepIconClasses: {
        completed: classes.completed,
      },
    },
    {
      isActive: location.pathname === ROUTES.TPA_MAKE_PAYMENT,
      isComplete: currentStep ? currentStep > 4 : false,
      label: t('TPA_STEPPER_LABEL_4'),
      stepIconClasses: {
        completed: classes.completed,
      },
    },
    {
      isActive: location.pathname === ROUTES.TPA_COMPLETE,
      isComplete: currentStep ? currentStep >= 5 : false,
      label: t('TPA_STEPPER_LABEL_5'),
      stepIconClasses: {
        completed: classes.completed,
      },
    },
  ];

  const getCurrentStepDetails = () => {
    switch (location.pathname) {
      case ROUTES.TPA_CREATE_PLAN:
        return { headline: t('TPA_STEPPER_1_HEADLINE'), tpaStep: 1 }; //Create your payment plan
        break;
      case ROUTES.TPA_REVIEW_PLAN:
        return { headline: t('TPA_STEPPER_2_HEADLINE'), tpaStep: 2 }; //Review your payment plan
        break;
      case ROUTES.TPA_CONFIRM_PLAN:
        return { headline: t('TPA_STEPPER_3_HEADLINE'), tpaStep: 3 }; //Complete your payment plan
        break;
      case ROUTES.TPA_MAKE_PAYMENT:
        return { headline: t('TPA_STEPPER_4_HEADLINE'), tpaStep: 4 }; //Make a down payment
        break;
      case ROUTES.TPA_COMPLETE:
        return { headline: t('TPA_STEPPER_5_HEADLINE'), tpaStep: 5 }; //Thank you for your payment
        break;
      default:
        return { headline: t(t('NMTTP')), tpaStep: null };
    }
  };

  const { accountParams } = useSelectedAccountParams();
  const { customer } = useAccountCustomer();
  useEffect(() => {
    setCurrentStep(0);
    setInstallmentAmount(0);
    setNumberOfMonths(DEFAULT_MONTHS);
    setTpaDetails(undefined);
    setDueDate(undefined);
    setCurrentChargesDue(undefined);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      accountParams && isTPAFeatureEnabled && refetch();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      shouldRedirect &&
        navigate(
          props?.landingURL ? props?.landingURL : ROUTES.NEED_MORE_TIME_TO_PAY,
        );
    })();
  }, [accountParams?.accountNumber]);

  const {
    loading: isLoadingTPADetails,
    data: getAccountDetailsData,
    error: getAccountDetailsError,
    refetch,
  } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getAccountDetailsQuery, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
      isTPAFeatureEnabled: isTPAFeatureEnabled,
    },
    skip: !accountParams || !isTPAFeatureEnabled,
    fetchPolicy: 'no-cache',
    errorPolicy: isTPAFeatureEnabled ? 'none' : 'ignore', // handling errors of graphql ref: https://www.apollographql.com/docs/react/data/error-handling/#setting-an-error-policy
    onError: () => setErrorNotification(true),
  });

  useEffect(() => {
    getCurrentStepDetails().tpaStep
      ? setIsShowTPAStepper(true)
      : setIsShowTPAStepper(false);

    setCurrentStep(getCurrentStepDetails().tpaStep);
  }, [location.pathname]);

  useEffect(() => {
    if (isTPAFeatureEnabled) {
      if (!isLoadingTPADetails && getAccountDetailsData) {
        const accountDetails = getAccountDetailsData?.getAccountDetails?.[0];
        if (accountDetails) {
          setTpaDetails(
            accountDetails.tpa
              ? mapTpaAccountDetail(accountDetails.tpa)
              : undefined,
          );
          setDueDate(
            moment(new Date(accountDetails.nextBill?.billDate))
              .subtract(1, 'day')
              .toDate(),
          );
          setCurrentChargesDue(accountDetails.currentCharges?.amountDue);
        }
      }
    }
  }, [isLoadingTPADetails, getAccountDetailsData]);

  useEffect(() => {
    tpaDetails?.recommendedPaymentMonths &&
      setNumberOfMonths(tpaDetails?.recommendedPaymentMonths);
  }, [tpaDetails?.recommendedPaymentMonths]);

  const [enrollTPA] = useMutation<Mutation>(enrollTPAMutation);
  const { wrapWithLoader } = useWrapWithLoader();
  const handleOnConfirmPlanClick = wrapWithLoader(
    async (): Promise<void> => {
      if (tpaConfirmationNumber) {
        return navigate(ROUTES.TPA_CONFIRM_PLAN);
      }
      setErrorNotification(false);
      const { arrangementAmount, downPaymentAmount } = calculateTpaAmounts(
        tpaDetails!.accountBalance!,
        numberOfMonths,
      );
      try {
        const queryResult: any = await enrollTPA({
          variables: {
            payload: {
              accountId: accountParams?.accountNumber,
              personId: customer?.personId,
              channel: Channel.Csweb,
              arrangementAmount,
              downPaymentAmount,
              installments: numberOfMonths,
              saType: SaType.Rartpa,
              allowSaTypes: true,
            },
          },
        });
        if (queryResult?.data?.enrollTpa) {
          const {
            confirmationNumber,
            enrolledInstallmentDetails,
          } = queryResult?.data?.enrollTpa;
          const paymentPlanConfirmationNumber = Number(confirmationNumber);
          const installmentPlan = enrolledInstallmentDetails?.monthsMatrix
            ? mapTPAMonthMatrix(enrolledInstallmentDetails?.monthsMatrix)
            : [];
          setTPAInstallmentPlan(installmentPlan);
          setTPAConfirmationNumber(paymentPlanConfirmationNumber);
          setTPADownPaymentAmount(downPaymentAmount);
          gtm_TPAReviewPlan_HandleOnConfirmPlanClick();
          return navigate(ROUTES.TPA_CONFIRM_PLAN);
        }
      } catch (e) {
        gtm_TPAReviewPlan_HandleOnConfirmPlanClickError();
        setErrorNotification(true);
      }
    },
  );

  const handleOnPaymentConfirmation = async (data: PaymentObject) => {
    gtm_TPAMakePayment_HandleOnPaymentConfirmation();
    const {
      oneTimePayConfirmationData,
      oneTimePayConfig,
      paymentMethodCapLimit,
    } = data;
    const updatedInstallmentPlan = setFirstPaymentToCompleted(
      tpaInstallmentPlan,
    );
    if (
      oneTimePayConfirmationData &&
      oneTimePayConfig &&
      paymentMethodCapLimit
    ) {
      setOneTimePayConfirmationData(oneTimePayConfirmationData);
      setOneTimePayConfig(oneTimePayConfig);
      setPaymentMethodCapLimit(paymentMethodCapLimit);
      setTPAInstallmentPlan(updatedInstallmentPlan);
    }
    await navigate(ROUTES.TPA_COMPLETE);
  };

  const handleOnReviewPlanClick = async (
    installmentAmt: number,
    numOfMonths: number,
  ) => {
    setInstallmentAmount(installmentAmt);
    setNumberOfMonths(numOfMonths);
    gtm_TPACreatePlan_HandleOnReviewPlanClick();
    await navigate(ROUTES.TPA_REVIEW_PLAN);
  };

  const handleCreatePlanOnBackClick = async (
    installmentAmt: number,
    numOfMonths: number,
  ) => {
    setInstallmentAmount(installmentAmt);
    setNumberOfMonths(numOfMonths);
    gtm_TPACreatePlan_HandleCreatePlanOnBackClick();
  };

  const handleReviewPlanOnBackClick = async () => {
    // So far, there are no actions needed since the navigate is on the ProceedOrCancel component in TPAReviewPlan.
    // Let's retain this here until we know that we truly don't need it and we can remove it then.
  };

  const isExistMailingAddress = (addressData: any) => {
    if (
      addressData &&
      (addressData?.addressLine1?.trim() ||
        addressData?.addressLine2?.trim()) &&
      (addressData?.postal?.trim() ||
        addressData?.city?.trim() ||
        addressData?.state?.trim())
    ) {
      return true;
    } else {
      return false;
    }
  };

  const mailingAddress = isExistMailingAddress(
    getAccountDetailsData?.getAccountDetails?.[0].mailingAddress,
  )
    ? getAccountDetailsData?.getAccountDetails?.[0].mailingAddress
    : // eslint-disable-next-line deprecation/deprecation
      getAccountDetailsData?.getAccountDetails?.[0].premiseInfo?.[0];

  return {
    progressTrackerSteps,
    getCurrentStepDetails,
    isLoadingTPADetails,
    tpaDetails,
    installmentAmount,
    setInstallmentAmount,
    numberOfMonths,
    currentStep,
    isShowTPAStepper,
    handleOnReviewPlanClick,
    handleCreatePlanOnBackClick,
    isTPAPlanSelected,
    setIsTPAPlanSelected,
    dueDate,
    currentChargesDue,
    handleOnConfirmPlanClick,
    handleReviewPlanOnBackClick,
    handleOnPaymentConfirmation,
    tpaConfirmationNumber,
    tpaInstallmentPlan,
    mailingAddress,
    oneTimePayConfirmationDataDetails,
    oneTimePayConfigDataDetails,
    paymentMethodCapLimitData,
    tpaDownPaymentAmount,
    getAccountDetailsError,
  };
};

/**
 * This function maps the GQL type Tpa_AccountDetail to the ui type TpaAccountDetail.
 * If GQL type changes happen that the implementation can be shielded from, we can 'intercept' them here.
 *
 * @param tpa the tpa data from the GQL query.
 * @returns the ui type TpaAccountDetail.
 */
function mapTpaAccountDetail(tpa: Tpa_AccountDetail): TpaAccountDetails {
  return {
    isEligible: tpa.isEligible,
    isEnrolled: tpa.isEnrolled,
    accountBalance: tpa.accountBalance ? tpa.accountBalance : undefined,
    recommendedPaymentMonths: tpa.recommendedPaymentMonths
      ? tpa.recommendedPaymentMonths
      : DEFAULT_MONTHS,
    enrolledInstallmentDetails: tpa.enrolledInstallmentDetails
      ? {
          remainingBalance: tpa.enrolledInstallmentDetails.payOffAmount,
          currentTpaMonthBalance: tpa.enrolledInstallmentDetails
            .currentTpaMonthBalance
            ? tpa.enrolledInstallmentDetails.currentTpaMonthBalance
            : undefined,
          totalMonths: tpa.enrolledInstallmentDetails.totalMonths,
          monthsMatrix: mapTPAMonthMatrix(
            tpa.enrolledInstallmentDetails.monthsMatrix,
          ),
        }
      : undefined,
  };
}

/**
 * This function maps the GQL type Tpa_MonthsMatrix to the ui type TpaMonthsMatrix.
 */
function mapTPAMonthMatrix(
  monthsMatrix: Tpa_MonthsMatrix[],
): TpaMonthsMatrix[] {
  return monthsMatrix.map(item => {
    return {
      monthNumber: item.monthNumber,
      isPaymentCompleted: item.isPaymentCompleted,
      paymentDate: new Date(item.paymentDate),
      monthlyAmount: item.monthlyAmount,
      doesBillExist: item.doesBillExist,
    };
  });
}
